// src/web/liveOccluder.js
//
// LIVE per-vertex face occluder — HYBRID approach (the correct one).
//
// THE PROBLEM WITH THE PREVIOUS LIVE OCCLUDER: it derived per-vertex DEPTH from
// an iris ruler, which lived in a different depth solve than the glasses (posed
// by the MediaPipe matrix M). Different depth frames → the occluder fought and
// clipped the lenses. That's why we fell back to the canonical occluder.
//
// THE PROBLEM WITH THE CANONICAL OCCLUDER: it's the AVERAGE face. Pinned at the
// bridge, but its cheek/jaw — far from the bridge anchor — swing away from the
// real face at angles (small rotation/shape error × big lever arm). Visible as a
// gap between the occluder and the real face at yaw.
//
// THE HYBRID FIX (this file):
//   - X, Y of each occluder vertex come from the REAL landmark, unprojected
//     through the scene camera. → the occluder traces the user's ACTUAL face
//     outline (real cheek/jaw/nose), closing the edge gap.
//   - The DEPTH each vertex is unprojected to comes from where the matrix M
//     places the corresponding CANONICAL vertex: depth_i = (M · canonicalVert_i).z.
//     → depth is in the SAME solve as the glasses (which are posed by M), so the
//     occluder and lenses share one depth frame → NO clipping, by construction.
//
// Result: real face SHAPE in the image plane + glasses-consistent DEPTH. This is
// the best of both — it fixes the edge gap without reintroducing lens clipping.

import {
  BufferGeometry, BufferAttribute, Mesh, MeshBasicMaterial, DoubleSide, Vector3, Matrix4
} from 'three';
import {
  FACEMESH_TESSELATION_INDICES, FACEMESH_NOSE_SIDES_INDICES, FACEMESH_MINUS_LENS_INDICES
} from './faceMeshTopology.js';
import { CANONICAL_FACE_VERTICES } from './canonicalFace.js';
import { warpedCanonical } from '../core/noseProfile.js';

const VERTS = 468;

export class LiveOccluder {
  constructor() {
    this.positions = new Float32Array(VERTS * 3);
    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));

    const qs = new URLSearchParams(window.location.search);
    const occRegion = (qs.get('occregion') || 'minuslens').toLowerCase();
    const indices =
        occRegion === 'full'      ? FACEMESH_TESSELATION_INDICES
      : occRegion === 'nosesides' ? FACEMESH_NOSE_SIDES_INDICES
      :                             FACEMESH_MINUS_LENS_INDICES;
    this.geometry.setIndex(new BufferAttribute(indices, 1));

    this.material = new MeshBasicMaterial({
      colorWrite: false, depthWrite: true, depthTest: true, side: DoubleSide
    });
    this._debugMaterial = new MeshBasicMaterial({
      color: 0x00ddff, colorWrite: true, transparent: true, opacity: 0.35,
      depthWrite: false, depthTest: true, side: DoubleSide
    });

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.renderOrder = 0;
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;   // verts already in world space
    this.mesh.visible = false;

    this.enabled = true;
    this._depthBias = (() => { const v = parseFloat(qs.get('occbias')); return Number.isFinite(v) ? v : 0.0; })();

    // Canonical vertices, optionally nose-warped per user (setNoseScale).
    this._canon = CANONICAL_FACE_VERTICES;
    this._noseScale = 1;

    // Scratch
    this._cv = new Vector3();       // canonical vertex
    this._ray = new Vector3();      // unproject ray dir
  }

  /**
   * @param {Array<{x,y,z}>} landmarks  478 normalized landmarks (real face)
   * @param {THREE.Matrix4} pinnedM     SAME matrix the glasses are posed by
   * @param {THREE.PerspectiveCamera} camera
   */
  update(landmarks, pinnedM, camera) {
    if (!this.enabled || !landmarks || landmarks.length < VERTS || !pinnedM) {
      this.mesh.visible = false; return;
    }
    const cv = this._canon;
    const e = pinnedM.elements;

    for (let i = 0; i < VERTS; i++) {
      const lm = landmarks[i];

      // Depth from M·canonicalVert_i (same solve as the glasses). Only the Z of
      // the transformed canonical vertex is needed.
      const cx = cv[i*3], cy = cv[i*3+1], cz = cv[i*3+2];
      // (M · [cx,cy,cz,1]).z  — column-major elements: z = e2*x+e6*y+e10*z+e14
      const depthZ = e[2]*cx + e[6]*cy + e[10]*cz + e[14];   // world Z (negative)
      const depth = Math.abs(depthZ) + this._depthBias;       // distance in front

      // Unproject the REAL landmark x,y at that depth through the camera.
      const ndcX = lm.x * 2 - 1;
      const ndcY = -(lm.y * 2 - 1);
      this._ray.set(ndcX, ndcY, 0.5).unproject(camera);
      this._ray.sub(camera.position).normalize();
      const t = -depth / this._ray.z;

      this.positions[i*3+0] = camera.position.x + this._ray.x * t;
      this.positions[i*3+1] = camera.position.y + this._ray.y * t;
      this.positions[i*3+2] = camera.position.z + this._ray.z * t;
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeVertexNormals();
    this.geometry.computeBoundingSphere();
    this.mesh.visible = true;
  }

  /**
   * The current occluder surface expressed in CANONICAL space (pinnedM⁻¹ ·
   * world), i.e. the person's real face outline with canonical depths. Used to
   * re-seat the frame against the real face (auto-seat + this = no lens corner
   * culled by fuller cheeks). Returns a new Float32Array(468*3) or null.
   */
  canonicalSurface(pinnedM) {
    if (!this.mesh.visible || !pinnedM) return null;
    const inv = this._inv || (this._inv = new Matrix4());
    inv.copy(pinnedM).invert();
    const out = new Float32Array(VERTS * 3);
    const v = this._cv;
    for (let i = 0; i < VERTS; i++) {
      v.set(this.positions[i*3], this.positions[i*3+1], this.positions[i*3+2]).applyMatrix4(inv);
      out[i*3] = v.x; out[i*3+1] = v.y; out[i*3+2] = v.z;
    }
    return out;
  }

  setEnabled(on) { this.enabled = on; if (!on) this.mesh.visible = false; }
  setDebugVisible(on) {
    this.mesh.material = on ? this._debugMaterial : this.material;
    this.mesh.renderOrder = on ? 20 : 0;
  }
  setDepthBias(cm) { this._depthBias = cm; }
  setNoseScale(r) {
    if (Math.abs(r - this._noseScale) < 1e-4) return;
    this._noseScale = r;
    this._canon = r === 1 ? CANONICAL_FACE_VERTICES : warpedCanonical(r);
  }
  getDepthBias() { return this._depthBias; }
  hide() { this.mesh.visible = false; }
  dispose() { this.geometry.dispose(); this.material.dispose(); }
}
